import { c as _c } from "react/compiler-runtime";
import { Button } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { toast } from '@repo/design-system/components/ui/toaster';
import { Type } from '@repo/design-system/components/ui/type';
import { css } from '@repo/panda/css';
import { useBulkUpdateMapPinsMutation } from '@repo/store/maps/api/mapsApi';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectExternalMapId, selectIsAddingMapLocation, selectIsBulkMovingLocations, selectIsBulkMovingSelection, selectIsBulkSelectingPins, selectIsInCreatorMode, selectIsMovingLocation, selectMapData, selectSelectedMarkerIds, setIsBulkMovingSelection, updateMapLocationFeatures } from '@repo/store/maps/slices/mapData';
import type { MapDataLocationFeature } from '@repo/types/maps/map-types';
import { findClosestPoint } from '@repo/types/maps/map-utils';
import * as MapLibre from 'maplibre-gl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PIN_INTERACTION_LAYER_IDS } from '../../game-map/hooks/constants';
import { useMapCursor } from '../../game-map/hooks/useMapCursor';
import { useMapContext } from '../../game-map/map-context';
import { toPersistentProperties } from './bulk-edit-pins-payload';
import { DraggableFloatingBar } from './draggable-floating-bar';
type Coordinates = [number, number];
type MercatorDelta = [number, number];
const MIN_MERCATOR_DELTA = 0.000000000001;
const getMercatorCoordinates = ([lng, lat]: Coordinates): MercatorDelta => {
  const coordinates = MapLibre.MercatorCoordinate.fromLngLat({
    lng,
    lat
  });
  return [coordinates.x, coordinates.y];
};
const translateCoordinates = ([lng, lat]: Coordinates, [xDelta, yDelta]: MercatorDelta): Coordinates => {
  const coordinates = MapLibre.MercatorCoordinate.fromLngLat({
    lng,
    lat
  });
  const translated = new MapLibre.MercatorCoordinate(coordinates.x + xDelta, coordinates.y + yDelta).toLngLat();
  return [translated.lng, translated.lat];
};
const hasMeaningfulDelta = ([xDelta, yDelta]: MercatorDelta) => Math.abs(xDelta) > MIN_MERCATOR_DELTA || Math.abs(yDelta) > MIN_MERCATOR_DELTA;
const getFeatureExternalId = (feature: {
  properties?: {
    externalId?: unknown;
  };
}) => typeof feature.properties?.externalId === 'string' ? feature.properties.externalId : null;

/** Button shown inside the bulk edit section once pins are selected. */
export const BulkMoveSelectedPinsAction = () => {
  const $ = _c(7);
  const dispatch = useAppDispatch();
  const isAddingMapLocation = useAppSelector(selectIsAddingMapLocation);
  const isBulkMovingLocations = useAppSelector(selectIsBulkMovingLocations);
  const isBulkMovingSelection = useAppSelector(selectIsBulkMovingSelection);
  const isBulkSelectingPins = useAppSelector(selectIsBulkSelectingPins);
  const isMovingLocation = useAppSelector(selectIsMovingLocation);
  const selectedMarkerIds = useAppSelector(selectSelectedMarkerIds);
  let t0;
  if ($[0] !== dispatch) {
    t0 = () => {
      dispatch(setIsBulkMovingSelection(true));
      toast({
        title: "Move selected pins",
        description: "Drag any one of the selected pins. They all move together.",
        variant: "info"
      });
    };
    $[0] = dispatch;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = selectedMarkerIds.length === 0 || isBulkMovingSelection || isBulkSelectingPins || isBulkMovingLocations || isAddingMapLocation || isMovingLocation;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = css({
      flex: "1",
      whiteSpace: "nowrap"
    });
    t3 = <Icon icon="open_with" decorative={true} />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t0 || $[5] !== t1) {
    t4 = <Button onClick={t0} color="warning" size="sm" disabled={t1} className={t2}>{t3}Move selected</Button>;
    $[4] = t0;
    $[5] = t1;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};

/** Drag handling and the floating bar, active only while moving a selection. */
export const BulkMoveSelectedPinsControls = () => {
  const $ = _c(62);
  const dispatch = useAppDispatch();
  const {
    map
  } = useMapContext();
  const {
    setCursor
  } = useMapCursor();
  const externalMapId = useAppSelector(selectExternalMapId);
  const isInCreatorMode = useAppSelector(selectIsInCreatorMode);
  const isBulkMovingSelection = useAppSelector(selectIsBulkMovingSelection);
  const selectedMarkerIds = useAppSelector(selectSelectedMarkerIds);
  const mapData = useAppSelector(selectMapData);
  const [bulkUpdateMapPins, t0] = useBulkUpdateMapPinsMutation();
  const {
    isLoading
  } = t0;
  const [hasMoved, setHasMoved] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const baseFeaturesRef = useRef(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [0, 0];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const currentDeltaRef = useRef(t2);
  const dragStartRef = useRef(null);
  const dragStartCursorRef = useRef(null);
  const dragPanWasEnabledRef = useRef(null);
  const isLoadingRef = useRef(isLoading);
  let t3;
  let t4;
  if ($[2] !== isLoading) {
    t3 = () => {
      isLoadingRef.current = isLoading;
    };
    t4 = [isLoading];
    $[2] = isLoading;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] !== selectedMarkerIds) {
    t5 = new Set(selectedMarkerIds);
    $[5] = selectedMarkerIds;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const selectedIdSet = t5;
  const mapDataRef = useRef(mapData);
  let t6;
  let t7;
  if ($[7] !== mapData) {
    t6 = () => {
      mapDataRef.current = mapData;
    };
    t7 = [mapData];
    $[7] = mapData;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  useEffect(t6, t7);
  let t8;
  if ($[10] !== map || $[11] !== selectedIdSet) {
    t8 = features => {
      if (!map) {
        return;
      }
      const markers = map.getSource("markers") as MapLibre.GeoJSONSource | undefined;
      markers?.setData({
        type: "FeatureCollection",
        features
      } as GeoJSON.FeatureCollection);
      const highlight = map.getSource("selected-marker") as MapLibre.GeoJSONSource | undefined;
      highlight?.setData({
        type: "FeatureCollection",
        features: features.filter(feature => selectedIdSet.has(feature.properties.externalId)).map(_temp)
      } as GeoJSON.FeatureCollection);
    };
    $[10] = map;
    $[11] = selectedIdSet;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const setSources = t8;
  let t9;
  if ($[13] !== selectedIdSet) {
    t9 = delta => baseFeaturesRef.current.map(feature_1 => selectedIdSet.has(feature_1.properties.externalId) ? {
      ...feature_1,
      geometry: {
        ...feature_1.geometry,
        coordinates: translateCoordinates(feature_1.geometry.coordinates, delta)
      }
    } : feature_1);
    $[13] = selectedIdSet;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const movedFeatures = t9;
  let t10;
  if ($[15] !== dispatch || $[16] !== setCursor || $[17] !== setSources) {
    t10 = () => {
      if (isLoadingRef.current) {
        return;
      }
      if (baseFeaturesRef.current.length > 0) {
        setSources(baseFeaturesRef.current);
      }
      currentDeltaRef.current = [0, 0];
      dragStartRef.current = null;
      setHasMoved(false);
      dispatch(setIsBulkMovingSelection(false));
      setCursor("grab");
    };
    $[15] = dispatch;
    $[16] = setCursor;
    $[17] = setSources;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const handleCancel = t10;
  let t11;
  if ($[19] !== bulkUpdateMapPins || $[20] !== dispatch || $[21] !== externalMapId || $[22] !== hasMoved || $[23] !== movedFeatures || $[24] !== selectedIdSet) {
    t11 = async () => {
      if (!externalMapId || !hasMoved) {
        return;
      }
      const updated = movedFeatures(currentDeltaRef.current).filter(feature_2 => selectedIdSet.has(feature_2.properties.externalId));
      ;
      try {
        await bulkUpdateMapPins({
          gameMapExternalId: externalMapId,
          pins: updated.map(_temp2)
        }).unwrap();
        dispatch(updateMapLocationFeatures(movedFeatures(currentDeltaRef.current)));
        dispatch(setIsBulkMovingSelection(false));
        baseFeaturesRef.current = [];
        currentDeltaRef.current = [0, 0];
        setHasMoved(false);
        toast({
          title: `${updated.length} pins moved`,
          variant: "success"
        });
      } catch (t12) {
        const error = t12;
        toast({
          title: "Move failed",
          description: error instanceof Error ? error.message : JSON.stringify(error),
          variant: "error"
        });
      }
    };
    $[19] = bulkUpdateMapPins;
    $[20] = dispatch;
    $[21] = externalMapId;
    $[22] = hasMoved;
    $[23] = movedFeatures;
    $[24] = selectedIdSet;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const handleConfirm = t11;
  let t12;
  let t13;
  if ($[26] !== handleCancel || $[27] !== isBulkMovingSelection || $[28] !== map || $[29] !== movedFeatures || $[30] !== selectedIdSet || $[31] !== setCursor || $[32] !== setSources) {
    t12 = () => {
      const currentMapData = mapDataRef.current;
      if (!isBulkMovingSelection || !map || !currentMapData) {
        return;
      }
      const targetMap = map;
      const markerLayers = PIN_INTERACTION_LAYER_IDS.filter(layer => targetMap.getLayer(layer));
      if (markerLayers.length === 0) {
        return;
      }
      baseFeaturesRef.current = [...currentMapData.geojson.features];
      currentDeltaRef.current = [0, 0];
      dragStartRef.current = null;
      setHasMoved(false);
      setCursor("grab");
      const handleMouseDown = e => {
        if (isLoadingRef.current) {
          return;
        }
        const candidates = e.features?.filter(feature_4 => {
          const externalId = getFeatureExternalId(feature_4);
          return feature_4.geometry.type === "Point" && externalId !== null && selectedIdSet.has(externalId);
        }) ?? [];
        if (candidates.length === 0) {
          return;
        }
        const closest = findClosestPoint(candidates, e.lngLat);
        if (!closest) {
          return;
        }
        e.preventDefault();
        dragStartRef.current = currentDeltaRef.current;
        dragStartCursorRef.current = getMercatorCoordinates([e.lngLat.lng, e.lngLat.lat]);
        dragPanWasEnabledRef.current = targetMap.dragPan.isEnabled();
        targetMap.dragPan.disable();
        setCursor("grabbing");
      };
      const handleMouseMove = e_0 => {
        const start = dragStartRef.current;
        const startCursor = dragStartCursorRef.current;
        if (!start || !startCursor) {
          return;
        }
        const cursor = getMercatorCoordinates([e_0.lngLat.lng, e_0.lngLat.lat]);
        const nextDelta = [start[0] + cursor[0] - startCursor[0], start[1] + cursor[1] - startCursor[1]];
        currentDeltaRef.current = nextDelta;
        setSources(movedFeatures(nextDelta));
      };
      const finishDrag = () => {
        if (!dragStartRef.current) {
          return;
        }
        setHasMoved(hasMeaningfulDelta(currentDeltaRef.current));
        dragStartRef.current = null;
        dragStartCursorRef.current = null;
        if (dragPanWasEnabledRef.current) {
          targetMap.dragPan.enable();
        }
        dragPanWasEnabledRef.current = null;
        setCursor("grab");
      };
      const handleKeyDown = e_1 => {
        if (e_1.key === "Escape") {
          e_1.preventDefault();
          handleCancel();
        }
      };
      targetMap.on("mousedown", markerLayers, handleMouseDown);
      targetMap.on("mousemove", handleMouseMove);
      targetMap.on("mouseup", finishDrag);
      document.addEventListener("mouseup", finishDrag);
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        targetMap.off("mousedown", markerLayers, handleMouseDown);
        targetMap.off("mousemove", handleMouseMove);
        targetMap.off("mouseup", finishDrag);
        document.removeEventListener("mouseup", finishDrag);
        document.removeEventListener("keydown", handleKeyDown);
        if (dragPanWasEnabledRef.current) {
          targetMap.dragPan.enable();
        }
        dragPanWasEnabledRef.current = null;
        setCursor("grab");
      };
    };
    t13 = [handleCancel, isBulkMovingSelection, map, movedFeatures, selectedIdSet, setCursor, setSources];
    $[26] = handleCancel;
    $[27] = isBulkMovingSelection;
    $[28] = map;
    $[29] = movedFeatures;
    $[30] = selectedIdSet;
    $[31] = setCursor;
    $[32] = setSources;
    $[33] = t12;
    $[34] = t13;
  } else {
    t12 = $[33];
    t13 = $[34];
  }
  useEffect(t12, t13);
  if (!isInCreatorMode || !isBulkMovingSelection) {
    return null;
  }
  let t14;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Icon icon="drag_indicator" size="sm" decorative={true} className={css({
      color: "whiteAlpha.40"
    })} />;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const t15 = selectedMarkerIds.length === 1 ? "" : "s";
  let t16;
  if ($[36] !== selectedMarkerIds.length || $[37] !== t15) {
    t16 = <Type variant="header-xxs" color="white">Moving {selectedMarkerIds.length} selected pin{t15}</Type>;
    $[36] = selectedMarkerIds.length;
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t14 || $[40] !== t16) {
    t17 = <FlexRow gap="4" align="center">{t14}{t16}</FlexRow>;
    $[39] = t14;
    $[40] = t16;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== hasMoved) {
    t18 = hasMoved && <Type variant="body-xs" color="text.secondary">Ready to save</Type>;
    $[42] = hasMoved;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t17 || $[45] !== t18) {
    t19 = <FlexRow gap="8" align="center" justify="between">{t17}{t18}</FlexRow>;
    $[44] = t17;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Type variant="body-xs" color="text.secondary">Drag any selected pin. The rest of the map stays where it is.</Type>;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== handleCancel || $[49] !== isLoading) {
    t21 = <Button variant="ghost" onClick={handleCancel} color="secondary" size="sm" disabled={isLoading}>Cancel</Button>;
    $[48] = handleCancel;
    $[49] = isLoading;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  const t22 = isLoading || !hasMoved;
  let t23;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Icon icon="check" decorative={true} />;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  const t24 = isLoading ? "Saving\u2026" : "Confirm move";
  let t25;
  if ($[52] !== handleConfirm || $[53] !== t22 || $[54] !== t24) {
    t25 = <Button onClick={handleConfirm} color="success" size="sm" disabled={t22}>{t23}{t24}</Button>;
    $[52] = handleConfirm;
    $[53] = t22;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t21 || $[57] !== t25) {
    t26 = <FlexRow gap="8" justify="end">{t21}{t25}</FlexRow>;
    $[56] = t21;
    $[57] = t25;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t19 || $[60] !== t26) {
    t27 = <DraggableFloatingBar><FlexColumn gap="10">{t19}{t20}{t26}</FlexColumn></DraggableFloatingBar>;
    $[59] = t19;
    $[60] = t26;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  return t27;
};
function _temp(feature_0) {
  return {
    ...feature_0,
    id: feature_0.properties.externalId,
    properties: {
      ...feature_0.properties,
      selected: true
    }
  };
}
function _temp2(feature_3) {
  return {
    externalId: feature_3.properties.externalId,
    properties: toPersistentProperties(feature_3.properties),
    geometry: feature_3.geometry
  };
}